import { useEffect, useRef } from "react";
import { PIN_LENGTH } from "@shared/pin";
import { cn } from "@/lib/cn";
import { BackspaceIcon } from "@/components/icons";

/*
 * Four dots and a round keypad. A hidden input takes a hardware keyboard or
 * a pasted PIN too. The parent submits on the fourth digit, after 180ms, so
 * the last dot is seen filling.
 */

export const AUTO_SUBMIT_MS = 180;

export function PinDots({ length, error }: { length: number; error?: boolean }) {
  return (
    <div className="flex justify-center gap-4" aria-label={`${length} of ${PIN_LENGTH} digits`} role="img">
      {Array.from({ length: PIN_LENGTH }, (_, i) => (
        <span
          key={i}
          className={cn(
            "size-[18px] rounded-full border-2 transition-colors duration-100",
            i < length ? "border-ink bg-ink" : "border-line-strong bg-transparent",
            error && "border-rhodo",
            error && i < length && "bg-rhodo",
          )}
        />
      ))}
    </div>
  );
}

export function PinPad({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const hidden = useRef<HTMLInputElement>(null);
  const press = (d: string) => {
    if (disabled || value.length >= PIN_LENGTH) return;
    onChange(value + d);
  };
  const back = () => !disabled && onChange(value.slice(0, -1));

  // Desktop keyboards and paste: the hidden field takes focus while the pad is
  // up. Not on a touch phone, where focusing it would pop the system keyboard over the pad.
  useEffect(() => {
    if (!disabled && window.matchMedia("(pointer: fine)").matches) hidden.current?.focus({ preventScroll: true });
  }, [disabled]);

  const key = "flex aspect-square w-full max-w-[84px] min-w-[66px] items-center justify-center rounded-full justify-self-center select-none";
  return (
    <div className="mx-auto w-full max-w-[320px]">
      <input
        ref={hidden}
        aria-label="PIN"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH))}
        className="absolute size-px opacity-0"
      />
      <div className="grid grid-cols-3 gap-x-5 gap-y-3.5">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" disabled={disabled} onClick={() => press(d)} className={cn(key, "border border-line-strong bg-surface font-mono text-[28px] active:bg-sunk disabled:opacity-50")}>
            {d}
          </button>
        ))}
        <span aria-hidden />
        <button type="button" disabled={disabled} onClick={() => press("0")} className={cn(key, "border border-line-strong bg-surface font-mono text-[28px] active:bg-sunk disabled:opacity-50")}>
          0
        </button>
        <button type="button" disabled={disabled} onClick={back} aria-label="Delete the last digit" className={cn(key, "text-muted active:bg-sunk disabled:opacity-50")}>
          <BackspaceIcon size={28} />
        </button>
      </div>
    </div>
  );
}
